// /compare/service-types — ตารางเปรียบเทียบ Notary / แปลรับรอง / NAATI / MFA-Apostille
import { CORE_SERVICE_LINKS, ROUTE_SERVICE_LINKS } from "@/data/tool-service-links";
import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { organizationSchema, speakableSchema, faqPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import ToolServiceBacklinks from "@/components/ToolServiceBacklinks";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/compare/service-types";
const TITLE = "เทียบชัด Notary vs แปลรับรอง vs NAATI vs MFA-Apostille ต่างกันอย่างไร";
const DESC =
  "ตารางเปรียบเทียบบริการรับรองเอกสาร 4 แบบ: ทนายผู้ทำคำรับรอง แปลรับรอง NAATI สำหรับออสเตรเลีย และการรับรองกงสุล/Apostille พร้อมสิ่งที่ต้องเตรียมและระยะเวลาโดยประมาณ";

const ROWS = [
  {
    k: "ทำอะไร",
    notary: "รับรองลายมือชื่อ สำเนาถูกต้อง ตัวบุคคล และข้อเท็จจริง โดยทนายความผู้ทำคำรับรองที่ขึ้นทะเบียนกับสภาทนายความฯ",
    trans: "แปลเอกสารและรับรองความถูกต้องของคำแปลโดยนักแปลที่ขึ้นทะเบียน พร้อมหนังสือรับรองคำแปล",
    naati: "แปลโดยนักแปลที่ได้รับการรับรองจาก NAATI ประเทศออสเตรเลีย พร้อมตราประทับและหมายเลข CPN",
    mfa: "กรมการกงสุลรับรองคำแปล/ลายมือชื่อ และออก Apostille สำหรับประเทศภาคีอนุสัญญา",
  },
  {
    k: "ใช้เมื่อไร",
    notary: "เอกสารเอกชน เช่น หนังสือมอบอำนาจ affidavit สัญญา เอกสารบริษัท ก่อนส่งไปใช้ต่างประเทศ",
    trans: "ทุกกรณีที่เอกสารต้นฉบับเป็นคนละภาษากับหน่วยงานผู้รับ",
    naati: "ยื่น Department of Home Affairs มหาวิทยาลัย หรือหน่วยงานออสเตรเลียเกือบทุกประเภท",
    mfa: "เมื่อต้องใช้เอกสารไทยในต่างประเทศ หรือใช้เอกสารต่างประเทศในไทย",
  },
  {
    k: "ระยะเวลาโดยประมาณ",
    notary: "ภายในวันเดียว (นัดหมายล่วงหน้า)",
    trans: "1–3 วันทำการ · มีบริการเร่งด่วน",
    naati: "1–3 วันทำการ",
    mfa: "กงสุล 2–5 วันทำการ · Apostille เพิ่ม 1–3 วันทำการ · สถานทูตปลายทางเพิ่ม 3–15 วันทำการ",
  },
  {
    k: "ต้องเตรียมอะไร",
    notary: "ตัวจริงของเอกสาร บัตรประชาชน/หนังสือเดินทางของผู้ลงนาม และผู้ลงนามต้องมาด้วยตนเอง",
    trans: "ไฟล์สแกนคมชัดของต้นฉบับ และการสะกดชื่อตามหนังสือเดินทาง",
    naati: "ต้นฉบับหรือสแกนคมชัด พร้อมหน้าพาสปอร์ตเพื่อยืนยันการสะกดชื่อ",
    mfa: "ต้นฉบับ + คำแปลที่รับรองแล้ว + สำเนาบัตรประชาชน/หนังสือเดินทาง และหนังสือมอบอำนาจกรณีให้ผู้อื่นยื่นแทน",
  },
  {
    k: "ผลลัพธ์ที่ได้",
    notary: "ตราประทับและลายมือชื่อทนายผู้ทำคำรับรอง พร้อมบันทึกในสมุดทะเบียน",
    trans: "คำแปลพร้อมหนังสือรับรองคำแปลและตราประทับ",
    naati: "คำแปลที่มีตรา NAATI ระบุหมายเลขนักแปลและวันที่",
    mfa: "ตรารับรองของกรมการกงสุล หรือใบ Apostille แนบท้าย",
  },
];

const FAQ = [
  {
    q: "Notary Public ในไทยกับ Notary Public ต่างประเทศเหมือนกันหรือไม่",
    a: "ไม่เหมือนกันในทางกฎหมาย ประเทศไทยไม่มีตำแหน่ง Notary Public แบบระบบคอมมอนลอว์ แต่ใช้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ที่ขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ ทำหน้าที่รับรองลายมือชื่อ สำเนาเอกสาร และข้อเท็จจริง ซึ่งหน่วยงานต่างประเทศยอมรับเมื่อผ่านการรับรองจากกรมการกงสุลและ Apostille หรือสถานทูตปลายทางตามเส้นทางที่ถูกต้อง",
  },
  {
    q: "ต้องเลือกอย่างใดอย่างหนึ่งหรือทำหลายอย่างพร้อมกัน",
    a: "ส่วนใหญ่ต้องทำต่อเนื่องกันเป็นลำดับ ไม่ใช่เลือกอย่างใดอย่างหนึ่ง เอกสารเอกชนเริ่มจากทนายผู้ทำคำรับรอง แล้วจึงแปลรับรอง จากนั้นกรมการกงสุลรับรอง และปิดท้ายด้วย Apostille หรือสถานทูตปลายทาง ส่วนเอกสารราชการไทยมักเริ่มที่การแปลรับรองได้เลยโดยไม่ต้องผ่านทนายผู้ทำคำรับรอง",
  },
  {
    q: "ถ้าจะยื่นออสเตรเลีย ต้องใช้ทั้ง NAATI และการรับรองกงสุลหรือไม่",
    a: "ขึ้นกับหน่วยงานผู้รับ การยื่นวีซ่ากับ Department of Home Affairs และการยื่นมหาวิทยาลัยส่วนใหญ่ต้องการคำแปลที่มีตรา NAATI เท่านั้น แต่ถ้าเอกสารต้องใช้กับหน่วยงานทะเบียนหรือศาลของออสเตรเลีย อาจต้องเพิ่มการรับรองจากกรมการกงสุลและ Apostille ด้วย จึงควรยืนยันข้อกำหนดกับหน่วยงานปลายทางก่อนเริ่มงาน",
  },
  {
    q: "Apostille เริ่มใช้กับเอกสารไทยตั้งแต่เมื่อไร",
    a: "ประเทศไทยเข้าเป็นภาคีอนุสัญญา Apostille โดยมีผลบังคับใช้ตั้งแต่วันที่ 15 ธันวาคม 2568 (15 ธันวาคม 2025) เอกสารที่จะใช้ในประเทศภาคีจึงขอ Apostille จากกรมการกงสุลได้โดยไม่ต้องผ่านสถานทูตปลายทางอีกชั้น ส่วนประเทศที่ไม่ใช่ภาคียังต้องใช้เส้นทางรับรองผ่านสถานทูตในไทยตามเดิม",
  },
  {
    q: "สาเหตุที่งานถูกตีกลับบ่อยที่สุดในทั้งสี่บริการคืออะไร",
    a: "สาเหตุหลักคือการสะกดชื่อไม่ตรงกับหนังสือเดินทาง คำแปลชื่อหน่วยงานหรือสถานที่ไม่สอดคล้องกันระหว่างเอกสารแต่ละฉบับ สำเนาไม่ชัดหรือตราประทับขาดหาย เอกสารเกินอายุที่หน่วยงานกำหนด และการข้ามลำดับขั้น เช่น นำเอกสารไปสถานทูตก่อนผ่านการรับรองของกรมการกงสุล",
  },
];

export const Route = createFileRoute("/compare/service-types")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      {
        name: "keywords",
        content: "notary กับ แปลรับรอง ต่างกันอย่างไร, naati คืออะไร, apostille ไทย, รับรองกงสุล, เปรียบเทียบรับรองเอกสาร",
      },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: canonicalAndHreflang(PATH),
    scripts: [
      consolidateGraph([
        organizationSchema(),
        speakableSchema([".aeo-speakable", "h1", "h2"]),
        faqPageSchema(FAQ),
      ]),
    ],
  }),
  component: CompareServiceTypes,
});

function CompareServiceTypes() {
  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:underline">หน้าแรก</Link> /{" "}
        <Link to="/compare" className="hover:underline">เปรียบเทียบ</Link> / ประเภทบริการรับรองเอกสาร
      </nav>
      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        Notary · แปลรับรอง · NAATI · MFA/Apostille ต่างกันอย่างไร
      </h1>
      <p className="aeo-speakable mt-3 text-muted-foreground">
        ทั้งสี่อย่างไม่ใช่ทางเลือกแทนกัน แต่เป็นคนละขั้นของสายรับรองเอกสาร
        ตารางด้านล่างสรุปว่าแต่ละบริการทำหน้าที่อะไร ใช้เมื่อไร ใช้เวลาโดยประมาณเท่าไร
        และต้องเตรียมอะไรไปบ้าง
      </p>

      <div className="mt-8 overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="py-3 pr-3">หัวข้อ</th>
              <th className="py-3 pr-3">ทนายผู้ทำคำรับรอง (Notary)</th>
              <th className="py-3 pr-3">แปลรับรอง</th>
              <th className="py-3 pr-3">NAATI (ออสเตรเลีย)</th>
              <th className="py-3">กงสุล / Apostille</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.k} className="border-b border-border/60 align-top">
                <td className="py-3 pr-3 font-semibold">{r.k}</td>
                <td className="py-3 pr-3 text-muted-foreground">{r.notary}</td>
                <td className="py-3 pr-3 text-muted-foreground">{r.trans}</td>
                <td className="py-3 pr-3 text-muted-foreground">{r.naati}</td>
                <td className="py-3 text-muted-foreground">{r.mfa}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="mt-10">
        <h2 className="text-xl font-semibold">ลำดับที่ถูกต้องของสายรับรอง</h2>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed">
          <li>เอกสารเอกชน: ลงนามต่อหน้าทนายผู้ทำคำรับรองก่อน (เอกสารราชการข้ามขั้นนี้ได้)</li>
          <li>แปลรับรองเป็นภาษาปลายทาง พร้อมล็อกคำศัพท์ชื่อบุคคลและหน่วยงาน</li>
          <li>กรมการกงสุลรับรองคำแปลและลายมือชื่อผู้แปล</li>
          <li>ปลายทางเป็นภาคีอนุสัญญา → ขอ Apostille · ไม่ใช่ภาคี → สถานทูตปลายทางในไทยรับรองต่อ</li>
        </ol>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold">คำถามที่พบบ่อย</h2>
        <div className="mt-3 space-y-4">
          {FAQ.map((f) => (
            <details key={f.q} className="rounded-lg border border-border bg-card p-4">
              <summary className="cursor-pointer text-sm font-medium">{f.q}</summary>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-lg font-semibold">แหล่งอ้างอิงทางการ</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
          <li><a className="underline" href="https://consular.mfa.go.th/" rel="nofollow noopener" target="_blank">กรมการกงสุล กระทรวงการต่างประเทศ</a></li>
          <li><a className="underline" href="https://www.lawyerscouncil.or.th/" rel="nofollow noopener" target="_blank">สภาทนายความในพระบรมราชูปถัมภ์</a></li>
          <li><a className="underline" href="https://www.naati.com.au/" rel="nofollow noopener" target="_blank">NAATI ประเทศออสเตรเลีย</a></li>
          <li><a className="underline" href="https://www.hcch.net/" rel="nofollow noopener" target="_blank">HCCH — อนุสัญญา Apostille</a></li>
        </ul>
      </section>

      <ToolServiceBacklinks
        heading="ไปยังหน้าบริการของแต่ละขั้นในสายรับรอง"
        lead="แต่ละคอลัมน์ในตารางด้านบนมีหน้าบริการของตัวเอง พร้อมรายละเอียดเอกสาร ขั้นตอน และคำถามที่พบบ่อยเฉพาะบริการนั้น"
        links={[
          ...CORE_SERVICE_LINKS,
          ...ROUTE_SERVICE_LINKS.naati,
          ...ROUTE_SERVICE_LINKS.embassy,
          ...ROUTE_SERVICE_LINKS.apostille,
        ]}
      />

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/tools/document-advisor" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
          ดูรายการเอกสารที่ต้องเตรียม
        </Link>
        <Link to="/tools/timeline" className="rounded-lg border border-border px-4 py-2 text-sm font-semibold">
          ประเมินระยะเวลา
        </Link>
      </div>
    </main>
  );
}
